import { Reveal, Overline } from "./Reveal";
import { bookService } from "./Services";
import { useSettings, imgSrc } from "@/lib/settings";
import { ArrowUpRight } from "lucide-react";

const STYLES = [
  { name: "Fine line", desc: "Traits ultra fins et délicats, une élégance discrète qui se porte comme un bijou." },
  { name: "Minimaliste", desc: "Petits motifs graphiques, symboles et mots — l'essentiel, rien de plus." },
  { name: "Floral", desc: "Compositions végétales féminines dessinées pour épouser les courbes du corps." },
  { name: "Lettering", desc: "Typographies fines et calligraphies personnalisées pour vos mots précieux." },
];

const BEFORE = [
  "Hydratez bien votre peau la semaine précédente",
  "Évitez alcool, soleil et gommages 48 h avant",
  "Mangez avant la séance, venez détendue",
  "Apportez vos inspirations — nous dessinons ensemble",
];

const AFTER = [
  "Appliquez la crème cicatrisante 2 à 3 fois par jour",
  "Ne grattez pas, n'exposez pas au soleil",
  "Pas de piscine, bain ou sauna pendant 3 semaines",
  "Retouche de contrôle offerte sous 2 mois si besoin",
];

export default function Tattoo() {
  const { settings } = useSettings();
  const gallery = settings.images.tattoo_gallery || [];
  return (
    <section id="tatouage" className="bg-[var(--brand-dark)] py-28 text-[var(--brand-bg)] lg:py-40" data-testid="tattoo-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <Reveal className="grid gap-12 lg:grid-cols-2 lg:items-end">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[var(--brand-accent)] mb-6">L'atelier tatouage</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              L'encre devient <span className="italic font-light text-[var(--brand-accent)]">poésie.</span>
            </h2>
          </div>
          <p className="max-w-md text-base font-light leading-relaxed text-[var(--brand-bg)]/70">
            Dans un espace dédié de l'institut, notre tatoueuse Maya imagine avec vous des tattoos délicats et féminins. Hygiène hospitalière, matériel stérile à usage unique et dessin sur mesure pour chaque projet.
          </p>
        </Reveal>

        <div className="mt-20 grid gap-6 md:grid-cols-3">
          {gallery.map((src, i) => (
            <Reveal key={src} delay={i * 0.1} className={i === 1 ? "md:translate-y-8" : ""}>
              <div className="group h-80 overflow-hidden">
                <img src={imgSrc(src)} alt={`Réalisation tatouage ${i + 1}`} data-testid={`tattoo-img-${i}`}
                     className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-28">
          <p className="text-xs uppercase tracking-[0.3em] text-[var(--brand-accent)] mb-6">Les styles</p>
          <h3 className="font-display text-3xl sm:text-4xl tracking-tight">Quatre univers, <span className="italic font-light text-[var(--brand-accent)]">une signature.</span></h3>
        </Reveal>
        <div className="mt-12 grid gap-px bg-[var(--brand-bg)]/10 sm:grid-cols-2 lg:grid-cols-4" data-testid="tattoo-styles">
          {STYLES.map((s, i) => (
            <Reveal key={s.name} delay={i * 0.08}>
              <div className="h-full bg-[var(--brand-dark)] p-8" data-testid={`tattoo-style-${i}`}>
                <h4 className="font-display text-2xl italic font-light text-[var(--brand-accent)]">{s.name}</h4>
                <p className="mt-4 text-sm font-light leading-relaxed text-[var(--brand-bg)]/65">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-28 grid gap-6 lg:grid-cols-2">
          <Reveal>
            <div className="h-full border border-[var(--brand-bg)]/10 p-10" data-testid="tattoo-before">
              <h4 className="font-display text-2xl">Avant la séance</h4>
              <ul className="mt-6 space-y-4">
                {BEFORE.map((item) => (
                  <li key={item} className="flex gap-4 text-sm font-light text-[var(--brand-bg)]/75">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--brand-accent)]" /> {item}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="h-full border border-[var(--brand-bg)]/10 p-10" data-testid="tattoo-after">
              <h4 className="font-display text-2xl">Après la séance</h4>
              <ul className="mt-6 space-y-4">
                {AFTER.map((item) => (
                  <li key={item} className="flex gap-4 text-sm font-light text-[var(--brand-bg)]/75">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--brand-accent)]" /> {item}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>

        <Reveal className="mt-20 flex flex-col items-center text-center">
          <button onClick={() => bookService("tatouage-fine-line")}
                  className="inline-flex items-center gap-3 rounded-full bg-[var(--brand-bg)] px-10 py-5 text-xs uppercase tracking-[0.25em] text-[var(--brand-dark)] transition-transform duration-300 hover:scale-[0.96]"
                  data-testid="tattoo-booking-btn">
            Demander un rendez-vous tatouage <ArrowUpRight size={16} />
          </button>
          <p className="mt-5 text-xs font-light uppercase tracking-[0.2em] text-[var(--brand-bg)]/50">
            Consultation & devis gratuits avant tout projet
          </p>
        </Reveal>
      </div>
    </section>
  );
}
